import { useCallback } from "react";
import { useSharedState } from "./useSharedState";

export function useSessionStorage<T>(key: string, initialValue: T) {
  return useWebStorage(
    key,
    initialValue,
    "session",
    typeof sessionStorage === "undefined" ? undefined : sessionStorage
  );
}

export function useLocalStorage<T>(key: string, initialValue: T) {
  return useWebStorage(
    key,
    initialValue,
    "local",
    typeof localStorage === "undefined" ? undefined : localStorage
  );
}

function useWebStorage<T>(
  key: string,
  initialValue: T,
  storageName: string,
  storage: Storage | undefined
): [T, (value: T) => void, () => T] {
  const [v, setState, getState] = useSharedState<T>("ws:" + key, () => {
    if (typeof storage !== "undefined") {
      try {
        const item = storage.getItem(key);
        if (item) {
          return JSON.parse(item) as T;
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error("storage [%s]: Could not get item", storageName, err);
      }
    }
    return initialValue;
  });

  const store = useCallback(function store(value: T) {
    if (typeof storage !== "undefined") {
      try {
        storage.setItem(key, JSON.stringify(value));
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error("storage [%s]: Could not set item", storageName, err);
      }
    }

    setState(value);
  }, []);

  return [v, store, getState];
}
